import * as React from 'react';
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText, Badge } from 'reactstrap';
import DeleteConfirmationModal from 'components/basic/modals/DeleteConfirmationModal';
import CloseConfirmationModal from 'components/basic/modals/CloseConfirmationModal';
import * as Clients from 'client/ApiClient';
import { CircularProgress } from '@mui/material';
import { HubConnection, HubConnectionBuilder } from '@microsoft/signalr'
import { enqueueNotification } from "../../store/Notifications"
import { store } from "AppContext"
import * as SourceSystem from 'constants/SourceSystem';


type ConversionAdministrationPopupProps = {
    conversionId?: string,
    isOpen: boolean,
    onClose: (ev: ConversionAdministrationPopupOnCloseEvent) => void
    currentUser: Clients.IUserLoginDetailsModel | undefined;
    conversionType?: string,
}

export type ConversionAdministrationPopupOnCloseEvent = {
    refresh: boolean,
}

type ConversionAdministrationPopupState = {
    conversion?: Clients.ConversionModel,
    deleteConfirmationModal: boolean,
    releaseConfirmationModal: boolean,
    loading: boolean,
    hubConnection: HubConnection | null;
    status: any;
    timeout: any;
    stage: boolean;
    refresh: boolean;
    buttonLock: boolean;
    options: { [key: string]: Clients.ConversionOptionValueModel } | undefined;
    refreshCall: string;
    imageStatus: any
}

export class ConversionAdministrationPopup extends React.Component<
    ConversionAdministrationPopupProps,
    ConversionAdministrationPopupState
>{
    private readonly conversionClient: Clients.ConversionClient = new Clients.ConversionClient();
    private readonly options: Clients.ConversionOptionsClient = new Clients.ConversionOptionsClient();
    private readonly conversonGenericClient: Clients.GenericClient = new Clients.GenericClient();
    private readonly executions: Clients.ExecutionsClient = new Clients.ExecutionsClient();

    constructor(props: Readonly<ConversionAdministrationPopupProps>) {
        super(props);
        this.state = {
            conversion: undefined,
            deleteConfirmationModal: false,
            releaseConfirmationModal: false,
            loading: false,
            hubConnection: null,
            status: null,
            timeout: null,
            stage: false,
            refresh: false,
            buttonLock: false,
            options: undefined,
            refreshCall: '',
            imageStatus: null,
        }
    }

    componentDidMount() {
        this.refreshCall();
    }

    refreshCall() {
        clearInterval(this.state.timeout);
        const link = '/executions';
        const refreshCall = this.props.conversionType && this.props.conversionType!.toUpperCase() === SourceSystem.epicToAMS360Id ? "00-00-00-00-Tier-Prep.dtsx" : "Orchestration-Map.dtsx"
        const hubConnection = new HubConnectionBuilder().withUrl(link).withAutomaticReconnect().build();
        this.setState({ hubConnection, refreshCall }, () => {
            this.state.hubConnection!
                .start()
                .then(() => console.log("Connection started for Administration"))
                .catch(() => console.log("there is an error"));

            this.state.hubConnection!.on("Administration", (execution: Clients.ExecutionsModel) => {
                clearInterval(this.state.timeout);
                this.buttonLock();
                if ((execution.description === "Succeeded" || execution.description === "Completed") && execution.userId === this.props.currentUser!.id) {
                    store.dispatch(enqueueNotification({ type: 'success', message: execution.clientDescription + ' - ' + execution.conversionDescription + ' - ' + execution.title + ' - ' + execution.description }))
                }
                else if ((execution.description === "Failed" || execution.description === "Canceled" || execution.description === "Ended Unexpectedly" || execution.description === "Stopping") && execution.userId === this.props.currentUser!.id) {
                    const color = execution.description === "Canceled" ? 'warning' : 'error';
                    store.dispatch(enqueueNotification({ type: color, message: execution.clientDescription + ' - ' + execution.conversionDescription + ' - ' + execution.title + ' - ' + execution.description }))
                }
                if (execution.conversionId === this.props.conversionId && execution.packageName === this.state.refreshCall) {
                    if (execution.endTime) {
                        clearInterval(this.state.timeout);
                        this.setState({
                            timeout: null, status: null
                        }, () => this.setStatus(execution!.estimatedFinish, execution!.description, execution!.endTime, 'refresh'))
                    } else if (execution.estimatedFinish) {
                        this.setState({ timeout: setInterval(() => { this.setStatus(execution!.estimatedFinish, execution!.description, execution!.endTime, 'refresh') }, 1000) })
                    } else if (execution.status === 2 && execution.startTime === null) {
                        clearInterval(this.state.timeout);
                        this.setStatus(execution!.estimatedFinish, execution!.description, execution!.endTime, 'refresh')
                    } else {
                        clearInterval(this.state.timeout);
                    }
                }
            });

            this.state.hubConnection!.on("ImageReport", (execution: Clients.ExecutionsModel) => {
                clearInterval(this.state.timeout);
                this.buttonLock();
                if ((execution.description === "Succeeded" || execution.description === "Completed") && execution.userId === this.props.currentUser!.id) {
                    store.dispatch(enqueueNotification({ type: 'success', message: execution.clientDescription + ' - ' + execution.conversionDescription + ' - ' + execution.title + ' - ' + execution.description }))
                }
                else if ((execution.description === "Failed" || execution.description === "Canceled" || execution.description === "Ended Unexpectedly" || execution.description === "Stopping") && execution.userId === this.props.currentUser!.id) {
                    const color = execution.description === "Canceled" ? 'warning' : 'error';
                    store.dispatch(enqueueNotification({ type: color, message: execution.clientDescription + ' - ' + execution.conversionDescription + ' - ' + execution.title + ' - ' + execution.description }))
                }
                if (execution.endTime) {
                    clearInterval(this.state.timeout);
                    this.setState({
                        timeout: null, imageStatus: null
                    }, () => this.setStatus(execution!.estimatedFinish, execution!.description, execution!.endTime, 'image'))
                } else {
                    this.setState({ timeout: setInterval(() => { this.setStatus(execution!.estimatedFinish, execution!.description, execution!.endTime, 'image') }, 1000) })
                }
            })

        })
    }

    componentWillUnmount() {
        this.state.hubConnection!.stop();
        clearInterval(this.state.timeout);
    }

    private setStatus(timeEstimate: Date | undefined, status: string | undefined, endTime: Date | undefined, type: string) {
        let color = ""
        if (endTime) {
            clearInterval(this.state.timeout);
            const dt = new Date(endTime);
            const day = dt.getDate(),
                month = dt.getMonth(),
                year = dt.getFullYear(),
                minutes = (dt.getMinutes() < 10 ? '0' : "") + dt.getMinutes();
            let hours = dt.getHours();
            const period = (hours < 12) ? 'AM' : 'PM';
            if (period === 'PM' && hours !== 12) hours = hours - 12;
            if (hours === 0) hours = 12;
            if (status === "Failed" || status === "Ended Unexpectedly") {
                color = "danger"
            } else if (status === "Canceled") {
                color = "warning"
            } else if (status === "Succeeded" || status === "Completed") {
                color = "success"
            }
            const title = type === 'image' ? 'Image report generated on ' : 'Migrate completed on '
            const displayStatus = < h6 className='status' > <Badge color={color}> {title} {
                `${(month + 1)}/${day}/${year} ${hours}:${minutes} ${period}`
            } - Status: {status} </Badge></h6>

            this.setState({
                timeout: null,
                loading: false,
            }, () => {
                if (type === 'image') {
                    this.setState({ imageStatus: displayStatus })
                } else {
                    this.setState({ status: displayStatus })
                }})
        } else {
            color = "primary"
            const now = new Date().valueOf();
            const estimate = timeEstimate ? new Date(timeEstimate).valueOf() : now;
            const timeRemain: number = (estimate - now) / 60000;
            const timeLeft = timeEstimate && timeRemain > 0 ? `${timeRemain.toFixed(2)} minutes` : "Pending";
            const title = type === 'image' ? 'Image report ' : 'Migrate '
            const displayStatus = < h6 className='status'> <Badge color={color}>{title} Estimate: {timeLeft} - Status: {status} </Badge></h6 >
            this.setState({
                loading: true,
            }, () => {
                if (type === 'image') {
                    this.setState({ imageStatus: displayStatus })
                } else {
                    this.setState({ status: displayStatus })
                }
            })
        }
    }

    componentDidUpdate(prevProps: ConversionAdministrationPopupProps) {
        if (prevProps.isOpen === false && this.props.isOpen === true) {
            clearInterval(this.state.timeout);
            this.setState({ refresh: false })
            this.updateConversion(this.props.conversionId);
            this.buttonLock();
            this.refreshCall();
            if (this.props.conversionId) {
                this.options.get(this.props.conversionId)
                    .then(values => {
                        const options = values!.reduce(
                            (result, current) => ({ ...result, [current.id!]: current, }), {});
                        this.setState({ options });
                    })
                    .catch(error => { });
            }
        }
    }

    private updateConversion = (conversionId?: string) => {
        if (conversionId) {
            this.conversionClient.get(conversionId)
                .then(response =>
                    this.setState({ conversion: response! }, () => {
                        this.checkConversion();
                    }))
                .catch((e) => { console.log(e) });
            this.executions!.listExecutions(conversionId)
                .then(results => {
                    if (results!.length === 0) {
                        this.setState({
                            loading: false,
                            status: null,
                            imageStatus: null,
                            refresh: true,
                        })
                    }
                    else {
                        const tierPrep = results!.filter(r => r.packageName === this.state.refreshCall)
                        let lastExecution = tierPrep![tierPrep!.length - 1];
                        if (lastExecution) {
                            const estimatedFinish = lastExecution.estimatedFinish;
                            const refreshEndTime = lastExecution.endTime;
                            if (refreshEndTime) clearInterval(this.state.timeout);

                            const clientsRan = results!.filter(r => r.packageName === '02-11-00-02-Client-Extract-Load.dtsx' || r.packageName === 'Orchestration-StageValidateAndLoad.dtsx' || r.packageName === 'Orchestration-LoadData.dtsx')
                            const refresh = clientsRan.length > 0 ? false : true;
                            this.setState({
                                refresh, timeout: setInterval(() => { this.setStatus(estimatedFinish, lastExecution.description!, refreshEndTime, 'refresh') }, 1000)
                            });

                            const imagePrep = results!.filter(r => r.packageName === 'Epic-MissingImages.dtsx')
                            let imageExecution = imagePrep![imagePrep!.length - 1];
                            if (imageExecution) {
                                const imageEndTime = imageExecution.endTime;
                                if (imageEndTime) clearInterval(this.state.timeout);

                                if (imageEndTime) {
                                    clearInterval(this.state.timeout);
                                    this.setStatus(imageExecution!.estimatedFinish, imageExecution!.description, imageEndTime, 'image')
                                    this.setStatus(estimatedFinish, lastExecution.description!, refreshEndTime, 'refresh')
                                } else {
                                    this.setState({
                                        timeout: setInterval(() => {
                                            this.setStatus(estimatedFinish, lastExecution.description!, refreshEndTime, 'refresh')
                                            this.setStatus(imageExecution!.estimatedFinish, imageExecution!.description, imageEndTime, 'image')
                                        }, 1000)
                                    })
                                }
                            }
                        }

                    }
                })
                .catch(e => { console.log(e) })

        } else {
            this.setState({ conversion: undefined });
        }
    }

    private refreshSourceTargetData = () => {
        this.setState({
            loading: true,
            status: null,
            imageStatus: null,
            buttonLock: true,
        }, () => {
            const conversion = this.state.conversion;
            const conversionType = conversion!.conversionTypeId!.toUpperCase() === SourceSystem.ams360ToAMS360Id || SourceSystem.ndtAms360ToAMS360Id === conversion!.conversionTypeId!.toUpperCase() ? 'merge' : 'epic'
            const user = this.props.currentUser;
            if (conversion && user) {
                this.conversonGenericClient.runConversionRefreshAction(conversion.id!, user.id!, conversionType)
                    .then(() => { })
                    .catch(() => { });
            }
        });
    }

    private deleteConversion = () => {
        const conversion = this.state.conversion;
        this.removeDeleteConfirmationModal();
        if (conversion) {
            this.conversionClient.delete(conversion.id!)
                .then(() => {
                    this.props.onClose({
                        refresh: true,
                    });
                })
                .catch(() => { });
        }
    }

    openDeleteConfirmationModal = () => {
        this.setState({ deleteConfirmationModal: true })
    }

    removeDeleteConfirmationModal = () => {
        this.setState({ deleteConfirmationModal: false });
    }

    private releaseConversion = () => {
        const conversion = this.state.conversion;
        this.removeReleaseConfirmationModal();

        if (conversion) {
            this.conversionClient.release(conversion.id!)
                .then(() => {
                    this.props.onClose({
                        refresh: true,
                    });
                })
                .catch(() => { });
        }
    }

    openReleaseConfirmationModal = () => {
        this.setState({ releaseConfirmationModal: true })
    }

    removeReleaseConfirmationModal = () => {
        this.setState({ releaseConfirmationModal: false });
    }

    checkConversion = () => {
        const conversion = this.state.conversion;
        if (conversion) {
            const conversionType = conversion.conversionTypeId!.toUpperCase() === SourceSystem.ams360ToAMS360Id || SourceSystem.ndtAms360ToAMS360Id === conversion.conversionTypeId!.toUpperCase() ? 'merge' : 'epic'
            this.conversionClient.getCheckInternalConnection(conversion!.staging || null, 'staging', conversionType)
                .then((result: boolean) => {
                    if (result) {
                        const stage = result
                        this.setState({ stage });
                    } else {
                        this.checkConversion()
                    }
                })
                .catch((err: any) => {
                    console.log(err)
                })
        }
    }

    buttonLock = () => {
        const conversionId = this.props.conversionId ? this.props.conversionId : '';
        if (conversionId !== '') {
            this.executions.listExecutionsAllTierBoolean(conversionId)
                .then((response) => {
                    if (response) {
                        this.setState({ buttonLock: response.isLocked })
                    }
                })
                .catch(error => { })
        }
    }

    toggleModal = () => {
        this.props.onClose({
            refresh: this.state.refresh && !this.state.conversion!.dateCompleted
        })
    }


    render() {
        const conversion = this.state.conversion ? this.state.conversion : undefined
        return (
            <Modal isOpen={this.props.isOpen} size="lg" toggle={() => this.toggleModal()} fade={true} >
                <ModalHeader toggle={() => this.toggleModal()} close={<button className="close" onClick={() => this.toggleModal()}>&times;</button>}>
                    Conversion: {this.state.conversion && this.state.conversion.description}
                </ModalHeader>
                <ModalBody className='no-border'>
                    <ListGroup flush>
                        {conversion !== undefined && this.state.stage && this.state.refresh && this.state.options && Object.keys(this.state.options).length > 0 && (this.state.conversion!.isCertified || this.state.conversion!.epicVersion === null) && this.state.conversion!.conversionTypeId.toUpperCase() !== SourceSystem.miniToAMS360Id ?
                            <ListGroupItem tag="button" action onClick={() => this.refreshSourceTargetData()} disabled={this.state.loading || this.state.buttonLock}>
                                <div className="custom-items">
                                    <ListGroupItemHeading>Refresh source and target data</ListGroupItemHeading>
                                    <CircularProgress color="inherit" className={this.state.loading ? "" : "custom-loading-icon"} size={20} />
                                    {conversion && conversion.imagePath === '' && this.state.status}
                                </div>
                                <div className="custom-items doublestatus">
                                    {conversion && conversion.imagePath !== '' && this.state.status}
                                    {conversion && conversion.imagePath !== '' && this.state.imageStatus}
                                </div>
                                <ListGroupItemText>Retrieve data from the currently identified connection string to both the source and target databases for all tiers.</ListGroupItemText>
                            </ListGroupItem> :
                            null
                        }
                        {this.state.options && Object.keys(this.state.options).length === 0 &&
                            <ListGroupItem tag="button" action disabled={true}>
                                <div className="custom-items">
                                    <ListGroupItemHeading>Refresh source and target data</ListGroupItemHeading>
                                </div>
                                <ListGroupItemText>Please submit conversion options to access refresh.</ListGroupItemText>
                            </ListGroupItem>
                        }
                        {this.state.conversion && !this.state.conversion!.dateCompleted && <ListGroupItem tag="button" action onClick={this.openReleaseConfirmationModal} disabled={this.state.buttonLock}>
                            <ListGroupItemHeading>Confirm conversion completed</ListGroupItemHeading>
                            <ListGroupItemText>Conversion is now completed and target database will be released</ListGroupItemText>
                        </ListGroupItem>}
                        <CloseConfirmationModal
                            isOpen={this.state.releaseConfirmationModal}
                            title={'Confirm Conversion Completed: ' + (this.state.conversion && this.state.conversion.description)}
                            message={'Please confirm to release ' + (this.state.conversion && this.state.conversion.description)}
                            onSubmit={this.releaseConversion}
                            onClose={this.removeReleaseConfirmationModal}
                        />
                        {this.state.conversion &&
                            <ListGroupItem tag="button" action onClick={this.openDeleteConfirmationModal} disabled={this.state.buttonLock && this.state.conversion && !this.state.conversion!.dateCompleted }>
                                <ListGroupItemHeading>Delete conversion</ListGroupItemHeading>
                                <ListGroupItemText>Delete conversion. This operation cannot be reverted.</ListGroupItemText>
                            </ListGroupItem>}
                    </ListGroup>
                    {conversion !== undefined ?
                        <DeleteConfirmationModal
                            isOpen={this.state.deleteConfirmationModal}
                            title={'Delete Conversion: ' + (this.state.conversion && this.state.conversion.description)}
                            message={'Please confirm to delete ' + (this.state.conversion && this.state.conversion.description)}
                            onSubmit={this.deleteConversion}
                            onClose={this.removeDeleteConfirmationModal}
                        /> : null}
                </ModalBody>
                <ModalFooter>
                    <Button
                        className='submit-button-orange'
                        onClick={() => this.props.onClose({
                            refresh: false,
                        })}
                    >Close</Button>
                </ModalFooter>
            </Modal>
        )
    }
}
